import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { HandHeart, CheckCircle, AlertTriangle, Upload, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const skillOptions = [
  'Working with children',
  'Working with adults',
  'Event planning',
  'Social media',
  'Photography or Videography',
  'First aid certified',
  'Administrative support',
  'Fundraising',
  'Public speaking',
  'Translation or Interpretation',
  'Counseling or Support',
  'Teaching or Education'
];

export default function Volunteer() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    reason: '',
    experience: '',
    availability: '',
    background_check_consent: false,
    police_clearance_file: null,
    emergency_contact_name: '',
    emergency_contact_phone: '',
    reference1_name: '',
    reference1_phone: '',
    reference2_name: '',
    reference2_phone: '',
    skills: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSkillToggle = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      // Check file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF, JPG, or PNG file');
        return;
      }
      setFormData(prev => ({ ...prev, police_clearance_file: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.background_check_consent) {
      setError('You must consent to a background check to volunteer');
      return;
    }

    if (!formData.reference1_name || !formData.reference1_phone || !formData.reference2_name || !formData.reference2_phone) {
      setError('Please provide two references with contact information');
      return;
    }

    setSubmitting(true);
    try {
      // Create form data for file upload
      const submitData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        reason: formData.reason,
        experience: formData.experience,
        availability: formData.availability,
        background_check_consent: formData.background_check_consent,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_phone: formData.emergency_contact_phone,
        skills: formData.skills,
        // Include references in the experience or reason field for now
        references: `Reference 1: ${formData.reference1_name} - ${formData.reference1_phone}, Reference 2: ${formData.reference2_name} - ${formData.reference2_phone}`
      };

      // Note: File upload would require multipart form data endpoint
      // For now, we'll note if they have a file
      if (formData.police_clearance_file) {
        submitData.experience = `${submitData.experience}\n\nPolice Clearance Certificate uploaded: ${formData.police_clearance_file.name}`;
      }

      submitData.experience = `${submitData.experience}\n\n${submitData.references}`;

      await axios.post(`${API}/volunteer/apply`, submitData);
      setSuccess(true);
      toast.success('Application submitted successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-8">
            <div className="w-20 h-20 rounded-full bg-[hsl(166,56%,90%)] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-[hsl(166,56%,45%)]" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Application Received!</h2>
            <p className="text-muted-foreground mb-6">
              Thank you for your interest in volunteering with Autism Acceptance! 
              We will review your application and contact you soon.
            </p>
            <Button onClick={() => navigate('/')} className="btn-primary">
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="container-main">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-[hsl(346,100%,90%)] text-[hsl(346,100%,45%)]">Join Our Team</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Volunteer Application</h1>
            <p className="text-muted-foreground text-lg">
              Thank you for your interest in volunteering with Autism Acceptance! 
              We host sensory-friendly events and need compassionate volunteers to support 
              autistic individuals, families, and children in the Cayman Islands.
            </p>
          </div>

          {/* Important Notice */}
          <Alert className="mb-8 border-[hsl(27,100%,55%)] bg-[hsl(27,100%,95%)]">
            <AlertTriangle className="h-4 w-4 text-[hsl(27,100%,45%)]" />
            <AlertDescription>
              These events involve working with vulnerable people. All volunteers must complete 
              a background check due to the involvement of vulnerable individuals and children.
            </AlertDescription>
          </Alert>

          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HandHeart className="h-6 w-6 text-primary" />
                Volunteer Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Personal Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      data-testid="volunteer-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      data-testid="volunteer-last-name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="volunteer-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="volunteer-phone"
                    />
                  </div>
                </div>

                {/* Skills */}
                <div className="space-y-3">
                  <Label>Skills & Experience (select all that apply)</Label>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {skillOptions.map((skill) => (
                      <label
                        key={skill}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${
                          formData.skills.includes(skill) 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Checkbox
                          checked={formData.skills.includes(skill)}
                          onCheckedChange={() => handleSkillToggle(skill)}
                        />
                        <span className="text-sm">{skill}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Availability */}
                <div className="space-y-2">
                  <Label htmlFor="availability">Availability *</Label>
                  <Select
                    value={formData.availability}
                    onValueChange={(value) => setFormData({ ...formData, availability: value })}
                  >
                    <SelectTrigger data-testid="volunteer-availability">
                      <SelectValue placeholder="Select your availability" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekdays">Weekdays only</SelectItem>
                      <SelectItem value="weekends">Weekends only</SelectItem>
                      <SelectItem value="both">Both weekdays and weekends</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Reason */}
                <div className="space-y-2">
                  <Label htmlFor="reason">Why do you want to volunteer with Autism Acceptance? *</Label>
                  <Textarea
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows={4}
                    required
                    data-testid="volunteer-reason"
                  />
                </div>

                {/* Experience */}
                <div className="space-y-2">
                  <Label htmlFor="experience">
                    Do you have any experience working with autistic individuals or neurodivergent communities?
                  </Label>
                  <Textarea
                    id="experience"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    rows={3}
                    placeholder="Please describe any relevant experience..."
                    data-testid="volunteer-experience"
                  />
                </div>

                {/* Emergency Contact */}
                <div className="p-4 bg-muted/30 rounded-xl space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Emergency Contact
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergency_name">Emergency Contact Name *</Label>
                      <Input
                        id="emergency_name"
                        value={formData.emergency_contact_name}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                        required
                        data-testid="volunteer-emergency-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency_phone">Emergency Contact Phone *</Label>
                      <Input
                        id="emergency_phone"
                        type="tel"
                        value={formData.emergency_contact_phone}
                        onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                        required
                        data-testid="volunteer-emergency-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* References */}
                <div className="p-4 bg-muted/30 rounded-xl space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    References (2 required)
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Please provide two references we can contact to verify your suitability for volunteering.
                  </p>
                  
                  {/* Reference 1 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ref1_name">Reference 1 Name *</Label>
                      <Input
                        id="ref1_name"
                        value={formData.reference1_name}
                        onChange={(e) => setFormData({ ...formData, reference1_name: e.target.value })}
                        required
                        placeholder="Full name"
                        data-testid="volunteer-ref1-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ref1_phone">Reference 1 Phone *</Label>
                      <Input
                        id="ref1_phone"
                        type="tel"
                        value={formData.reference1_phone}
                        onChange={(e) => setFormData({ ...formData, reference1_phone: e.target.value })}
                        required
                        placeholder="Contact number"
                        data-testid="volunteer-ref1-phone"
                      />
                    </div>
                  </div>

                  {/* Reference 2 */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ref2_name">Reference 2 Name *</Label>
                      <Input
                        id="ref2_name"
                        value={formData.reference2_name}
                        onChange={(e) => setFormData({ ...formData, reference2_name: e.target.value })}
                        required
                        placeholder="Full name"
                        data-testid="volunteer-ref2-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ref2_phone">Reference 2 Phone *</Label>
                      <Input
                        id="ref2_phone"
                        type="tel"
                        value={formData.reference2_phone}
                        onChange={(e) => setFormData({ ...formData, reference2_phone: e.target.value })}
                        required
                        placeholder="Contact number"
                        data-testid="volunteer-ref2-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Police Clearance Upload */}
                <div className="p-4 bg-muted/30 rounded-xl space-y-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Police Clearance Certificate
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    If you have a valid police clearance certificate, please upload it here. 
                    Accepted formats: PDF, JPG, PNG (max 5MB)
                  </p>
                  <div className="flex items-center gap-4">
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileChange}
                      className="cursor-pointer"
                      data-testid="volunteer-police-clearance"
                    />
                  </div>
                  {formData.police_clearance_file && (
                    <p className="text-sm text-[hsl(166,56%,45%)]">
                      Selected: {formData.police_clearance_file.name}
                    </p>
                  )}
                </div>

                {/* Background Check Consent */}
                <div className="p-4 bg-[hsl(27,100%,95%)] rounded-xl space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="background_check"
                      checked={formData.background_check_consent}
                      onCheckedChange={(checked) => setFormData({ ...formData, background_check_consent: checked })}
                      className="mt-1"
                      data-testid="volunteer-background-check"
                    />
                    <Label htmlFor="background_check" className="cursor-pointer">
                      <span className="font-semibold">I consent to a background check *</span>
                      <p className="text-sm text-muted-foreground mt-1">
                        I understand that a background check is required for all volunteers working with 
                        vulnerable individuals and children. I consent to this check being performed.
                      </p>
                    </Label>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={submitting}
                  data-testid="volunteer-submit"
                >
                  {submitting ? 'Submitting...' : (
                    <>
                      <HandHeart className="mr-2 h-4 w-4" />
                      Submit Application
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
